// 알림센터 화면.
// 작업계획서 4.6/4.7: 공지/이벤트 + 주문 상태 전환 알림을 모아 보여준다. 진입 시 전체 읽음 처리.

import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "@oo/ui";
import type { NotificationDto } from "@oo/shared";
import {
  clearAllNotifications,
  getNotifications,
  markAllNotificationsRead,
} from "@/features/notifications/notificationsApi.ts";

// 알림센터 화면.
export default function NotificationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const query = useQuery({ queryKey: ["notifications"], queryFn: getNotifications });

  // 화면 진입 시 전체 읽음 처리 (배지 제거).
  const readMut = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  useEffect(() => {
    // unread 상태가 잡힌 직후 1회 전체 읽음 처리.
    if (query.data && query.data.unread > 0) {
      readMut.mutate();
    }
  }, [query.data?.unread, readMut, query.data]);

  // 알림 비우기(전체 삭제). 되돌릴 수 없으므로 확인 모달을 거친다.
  const clearMut = useMutation({
    mutationFn: clearAllNotifications,
    onSuccess: () => {
      // 서버 재조회(invalidate)만으로는 응답이 늦으면 화면이 멈춘 듯 보이므로(개선항목 13),
      // 캐시를 즉시 빈 목록으로 덮어써 화면을 바로 갱신한 뒤 백그라운드로 재검증한다.
      qc.setQueryData(["notifications"], { notifications: [], unread: 0 });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
  async function onClear() {
    const ok = await confirm({
      title: "알림을 모두 비울까요?",
      description: "받은 알림이 모두 삭제됩니다. 이 동작은 되돌릴 수 없어요.",
      confirmLabel: "비우기",
      cancelLabel: "취소",
      tone: "danger",
    });
    if (ok) {
      clearMut.mutate();
    }
  }

  const notifications = query.data?.notifications ?? [];

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-3 px-4 py-6 pb-28">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">알림</h1>
        {/* 알림이 있을 때만 비우기 버튼 노출 */}
        {notifications.length > 0 ? (
          <button
            type="button"
            onClick={onClear}
            disabled={clearMut.isPending}
            className="shrink-0 rounded-md border border-ink-300 px-3 py-1.5 text-sm text-ink-700 hover:bg-ink-100 disabled:opacity-50"
          >
            {clearMut.isPending ? "비우는 중..." : "알림 비우기"}
          </button>
        ) : null}
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-ink-100" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-300 p-10 text-center text-sm text-ink-600">
          받은 알림이 없습니다.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {notifications.map((n) => (
            <NotificationRow key={n.id} n={n} onClick={() => n.linkUrl && navigate(n.linkUrl)} />
          ))}
        </ul>
      )}

      <Link to="/me" className="text-center text-sm text-ink-600 underline">
        마이페이지로
      </Link>
    </main>
  );
}

function NotificationRow({ n, onClick }: { n: NotificationDto; onClick: () => void }) {
  const dateLabel = n.createdAt.slice(0, 16).replace("T", " ");
  const clickable = !!n.linkUrl;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={!clickable}
        className={`flex w-full flex-col items-start gap-1 rounded-lg border p-4 text-left ${
          n.read ? "border-ink-200 bg-white" : "border-brand-600 bg-ink-50"
        } ${clickable ? "hover:bg-ink-100" : "cursor-default"}`}
      >
        <div className="flex w-full items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                n.kind === "order" ? "bg-brand-600 text-white" : "bg-warning-50 text-warning-700"
              }`}
            >
              {n.kind === "order" ? "주문" : "공지"}
            </span>
            <span className="text-sm font-semibold">{n.title}</span>
          </span>
          {!n.read ? <span className="h-2 w-2 shrink-0 rounded-full bg-brand-600" /> : null}
        </div>
        <p className="whitespace-pre-wrap text-sm text-ink-700">{n.body}</p>
        <p className="text-xs text-ink-600">{dateLabel}</p>
      </button>
    </li>
  );
}
